import { LogIn } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Navigate, useLocation } from 'react-router'

import { Anchor } from '@/components/ui/Anchor'
import { Button } from '@/components/ui/button'
import { SkeletonFields } from '@/components/ui/skeleton'
import { appHref, marketingHref } from '@/config/config'
import { INSURANCE_VERTICAL_SLUGS, type InsuranceVerticalSlug } from '@/config/insuranceVerticals'
import { trackMarketingCta } from '@/lib/analytics'
import { useHostGuard } from '@/lib/hostGuard'
import { CaiProductLinks } from './components/CaiProductLinks'
import { CoiRequestVisual } from './components/CoiRequestVisual'
import { HubSpotFormEmbed } from './components/HubSpotFormEmbed'
import { LogoGrid } from './components/LogoGrid'
import { NavBar } from './components/NavBar'
import { QuoteFormConsent } from './components/QuoteFormConsent'
import { QuoteFormUnavailable } from './components/QuoteFormUnavailable'
import { Reveal } from './components/Reveal'
import { SiteFooter } from './components/SiteFooter'
import { VerticalAudiences } from './components/VerticalAudiences'
import { VerticalCoverageList } from './components/VerticalCoverageList'
import { CONTRACTOR_CARRIER_LOGOS } from './content/carrierLogos'
import { INSURANCE_VERTICALS, type InsuranceVertical as Vertical } from './content/insuranceVerticals'
import {
  SITE_FOOTER_LEGAL_LINKS,
  SITE_FOOTER_LINK_COLUMNS,
  SITE_FOOTER_NOTE,
  SITE_FOOTER_TAGLINE,
} from './content/siteFooterContent'

/* Standalone contractor-trade insurance landing pages at /insurance/<vertical> — own header and footer, no MarketingLayout. */

const PHONE_NUMBER = '(770) 765-0331'
const FORM_ANCHOR = '#get-quotes'

const HUBSPOT_PORTAL_ID = '246335544'
const HUBSPOT_REGION = 'na2'

// The trades the application asks about today; the others keep their HubSpot form.
const APPLICATION_TRADES: readonly InsuranceVerticalSlug[] = ['hvac']

/** The quote button's target: the application, once the backend offers it to this visitor, else the form.
 *  Read after mount: the prerendered page has no config, so it always starts on the form. */
function useQuoteHref(slug: InsuranceVerticalSlug | undefined): string {
  const [href, setHref] = useState(FORM_ANCHOR)
  useEffect(() => {
    const offered = slug && APPLICATION_TRADES.includes(slug) && window.__CAI_CONFIG__?.contractorApplication
    // A flag testing parameter (dwft_<flag>) that turned this on rides along to the app's host.
    const query = new URLSearchParams({ trade: slug ?? '' })
    for (const [name, value] of new URLSearchParams(window.location.search)) {
      if (name.startsWith('dwft_')) query.set(name, value)
    }
    setHref(offered ? appHref(`/application/start?${query.toString()}`) : FORM_ANCHOR)
  }, [slug])
  return href
}

function verticalFor(pathname: string): Vertical | undefined {
  const slug = pathname.replace(/\/+$/, '').split('/').at(-1)
  return INSURANCE_VERTICAL_SLUGS.includes(slug as InsuranceVerticalSlug)
    ? INSURANCE_VERTICALS[slug as InsuranceVerticalSlug]
    : undefined
}

export function InsuranceVertical() {
  const { pathname } = useLocation()
  const { redirecting, navigateTo } = useHostGuard('marketing')
  const vertical = verticalFor(pathname)
  const quoteHref = useQuoteHref(vertical?.slug)
  if (navigateTo) return <Navigate to={navigateTo} replace />
  if (redirecting) return null
  if (!vertical) return <Navigate to="/insurance" replace />

  const cta = (placement: string) => trackMarketingCta(`insurance_${vertical.slug}_${placement}`)

  return (
    <div className="bg-ink-50 min-h-screen w-full shrink-0">
      <title>{`Cai | ${vertical.pageTitle}`}</title>
      <meta property="og:title" content={`Cai | ${vertical.pageTitle}`} />
      <meta property="og:description" content={vertical.metaDescription} />
      <meta property="og:image" content={marketingHref(vertical.shareImage)} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="description" content={vertical.metaDescription} />

      <NavBar
        theme="dark"
        phoneNumber={PHONE_NUMBER}
        onPhoneClick={() => cta('nav_phone')}
        utilityLinks={[{ label: 'Login', icon: <LogIn />, href: appHref('/insure') }]}
        ctaLabel="Get your quotes"
        ctaHref={quoteHref}
        onCtaClick={() => cta('nav_get_quote')}
      />

      <section className="bg-ink-900 px-5.5 pt-16 pb-24 lg:pt-26 lg:pb-30">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-2 items-center gap-16 max-lg:grid-cols-1 max-lg:gap-12">
          <Reveal className="flex flex-col gap-7">
            <span className="eyebrow-on-dark">{vertical.trade} insurance</span>
            <h1 className="h1-hero text-balance text-white">
              Insurance that doesn’t hold your business back
            </h1>
            <p className="text-ink-100 max-w-125 text-xl leading-relaxed text-pretty">{vertical.heroSub}</p>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-4">
              <Button size="lg" href={quoteHref} onClick={() => cta('hero_get_quote')}>
                Get your quotes
              </Button>
              <span className="text-ink-300 font-mono text-xs font-bold tracking-[0.1em] uppercase">
                Apply in minutes · No requirement to buy
              </span>
            </div>
          </Reveal>
          <Reveal delay={150} className="relative aspect-square w-full overflow-hidden rounded-sm bg-white/5">
            <img
              src={vertical.heroImage}
              alt={vertical.heroImageAlt}
              className="absolute inset-0 h-full w-full object-cover"
              fetchPriority="high"
            />
          </Reveal>
        </div>
      </section>

      <section className="border-ink-100 border-b bg-white">
        <LogoGrid heading="We shop A-rated insurance providers for you" logos={CONTRACTOR_CARRIER_LOGOS} />
      </section>

      <VerticalAudiences heading={vertical.audienceHeading} audiences={vertical.audiences} />

      <section className="bg-ink-50 px-5.5 py-24 lg:py-30">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-2 items-center gap-16 max-lg:grid-cols-1 max-lg:gap-12">
          <Reveal className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <span className="eyebrow self-start">Coverages</span>
              <h2 className="h2 text-balance">One insurance broker for all the coverage you need</h2>
            </div>
            <VerticalCoverageList coverages={vertical.coverages} />
          </Reveal>
          <Reveal delay={150} className="bg-ink-100 relative aspect-[4/5] w-full overflow-hidden rounded-sm">
            <img
              src={vertical.coveragesImage}
              alt={vertical.coveragesImageAlt}
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
          </Reveal>
        </div>
      </section>

      <section className="bg-ink-900 px-5.5 py-24 lg:py-30">
        <div className="mx-auto grid max-w-(--maxw) grid-cols-2 items-center gap-16 max-lg:grid-cols-1 max-lg:gap-12">
          <Reveal className="flex aspect-square w-full items-center justify-center rounded-sm bg-white/5 p-10 max-lg:order-last max-md:aspect-auto max-md:p-5">
            <CoiRequestVisual request={vertical.coiRequest} />
          </Reveal>
          <Reveal delay={150} className="flex flex-col gap-6">
            <p className="h3 text-brand-300">{vertical.coiSetup}</p>
            <h2 className="h2 text-balance text-white">
              With Cai as your insurance broker, COIs are instant
            </h2>
            <p className="text-ink-100 max-w-120 text-xl leading-relaxed text-pretty">{vertical.coiBody}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-white px-5.5 py-24 lg:py-30">
        <div className="mx-auto flex max-w-(--maxw) flex-col gap-14">
          <Reveal className="flex max-w-180 flex-col gap-4">
            <h2 className="h2 text-balance">Intelligent {vertical.trade} insurance</h2>
            <Anchor
              href="https://blog.cai.ai/how-cai-is-using-ai-to-fix-trucking-insurance"
              onClick={() => cta('how_we_use_ai')}
              className="text-brand-800 hover:text-ink text-md w-fit font-bold no-underline transition-colors duration-150"
            >
              See how Cai uses AI <span aria-hidden="true">→</span>
            </Anchor>
          </Reveal>
          <Reveal delay={120} className="grid grid-cols-3 gap-12 max-md:grid-cols-1 max-md:gap-8">
            {vertical.features.map((feature) => (
              <div key={feature.title} className="border-ink-100 flex flex-col gap-3 border-t pt-6">
                <h3 className="text-ink text-2xl font-bold tracking-[-0.01em] text-balance">
                  {feature.title}
                </h3>
                <p className="text-md text-ink-600 leading-relaxed text-pretty">{feature.body}</p>
              </div>
            ))}
          </Reveal>
        </div>
      </section>

      <section id="get-quotes" className="bg-brand-900 scroll-mt-20 px-5.5 py-24 lg:py-30">
        <div className="mx-auto flex max-w-(--maxw) flex-col gap-12">
          <Reveal className="flex flex-col items-start gap-7">
            <h2 className="h2 text-balance text-white">Let us shop around for you</h2>
            <p className="text-ink-50 text-xl leading-relaxed">
              Tell us a little bit about your business, and a Cai broker will follow up ASAP.
            </p>
          </Reveal>
          <Reveal delay={150} className="min-h-105 w-full max-w-180 rounded-sm bg-white p-8 max-md:p-5">
            <HubSpotFormEmbed
              key={vertical.hubspotFormId}
              portalId={HUBSPOT_PORTAL_ID}
              formId={vertical.hubspotFormId}
              region={HUBSPOT_REGION}
              onSubmitted={() => cta('form_submitted')}
              fallback={<SkeletonFields fields={8} />}
              failedFallback={<QuoteFormUnavailable phoneNumber={PHONE_NUMBER} />}
            />
            <QuoteFormConsent />
          </Reveal>
        </div>
      </section>

      <CaiProductLinks ctaPrefix={`insurance_${vertical.slug}`} />

      <SiteFooter
        hideEdgeLines
        tagline={SITE_FOOTER_TAGLINE}
        linkColumns={SITE_FOOTER_LINK_COLUMNS}
        legalLinks={SITE_FOOTER_LEGAL_LINKS}
        note={SITE_FOOTER_NOTE}
      />
    </div>
  )
}
